const API_BASE = import.meta.env.VITE_API_BASE ?? '/api/v1'

/** URL картинки аватара (GET /avatars/{user}/{hash}); undefined — аватара нет, показываем инициал. */
export function avatarUrl(userId: string, hash: string | null | undefined): string | undefined {
  return hash ? `${API_BASE}/avatars/${userId}/${hash}` : undefined
}

/*
 * Аватар участника на конкретном сервере («Кличат Плюс»): если он задан, показываем его,
 * иначе — обычный из профиля. Ключ по серверу и хешу картинки, как у иконки сервера.
 */
export function memberAvatarUrl(
  guildId: string | null | undefined,
  member: { avatar?: string | null; user: { id: string; avatar?: string | null } } | null | undefined,
): string | undefined {
  if (!member) return undefined
  if (guildId && member.avatar) return `${API_BASE}/member-avatars/${guildId}/${member.avatar}`
  return avatarUrl(member.user.id, member.user.avatar)
}

/** Иконка сервера: тот же приём, что и с аватаром — смена хэша меняет адрес. */
export function guildIconUrl(guildId: string, hash: string | null | undefined): string | undefined {
  return hash ? `${API_BASE}/icons/${guildId}/${hash}` : undefined
}

/** Баннер сервера — картинка над списком каналов и в карточке каталога. */
export function guildBannerUrl(guildId: string, hash: string | null | undefined): string | undefined {
  return hash ? `${API_BASE}/banners/${guildId}/${hash}` : undefined
}

/** Баннер профиля: у серверов свой /banners, у людей — /user-banners. */
export function userBannerUrl(userId: string, hash: string | null | undefined): string | undefined {
  return hash ? `${API_BASE}/user-banners/${userId}/${hash}` : undefined
}

/** Ужимает картинку до квадрата side×side (обрезка по центру) и отдаёт data URI PNG для PATCH /users/@me. */
export async function fitAvatar(file: File, side = 256): Promise<string> {
  const bitmap = await createImageBitmap(file)
  try {
    const size = Math.min(bitmap.width, bitmap.height)
    const sx = (bitmap.width - size) / 2
    const sy = (bitmap.height - size) / 2
    const canvas = document.createElement('canvas')
    canvas.width = side
    canvas.height = side
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Не удалось обработать картинку')
    ctx.drawImage(bitmap, sx, sy, size, size, 0, 0, side, side)
    return canvas.toDataURL('image/png')
  } finally {
    bitmap.close()
  }
}

/** Файл целиком как data URI: GIF-баннер уходит без перерисовки, чтобы не потерять движение. */
export function fileDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error ?? new Error('Не удалось прочитать файл'))
    reader.readAsDataURL(file)
  })
}

/** Ужимает картинку под баннер профиля (обрезка по центру) и отдаёт data URI JPEG — PNG такого размера не влезает в лимит. */
export async function fitBanner(file: File, width = 1200, height = 400): Promise<string> {
  const bitmap = await createImageBitmap(file)
  try {
    const scale = Math.max(width / bitmap.width, height / bitmap.height)
    const sw = Math.min(bitmap.width, width / scale)
    const sh = Math.min(bitmap.height, height / scale)
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Не удалось обработать картинку')
    ctx.drawImage(bitmap, (bitmap.width - sw) / 2, (bitmap.height - sh) / 2, sw, sh, 0, 0, width, height)
    return canvas.toDataURL('image/jpeg', 0.9)
  } finally {
    bitmap.close()
  }
}
